
import { Filter, Users, FileText, CheckCircle, Footprints, ArrowRight, ArrowDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { localToday } from "@/lib/data";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  pct, th, td, useRange, useReport, yearRange, type ExportSpec,
} from "../ReportKit";

interface Stage { count: number; quoted: number; booked: number }
interface Funnel {
  funnel: {
    enquiries: { total: number; new: number; qualified: number; converted: number; rejected: number };
    quoted: number;
    booked: number;
    booked_after_quote: number;
    quotations: Record<string, number>;
    events: { booked: number; draft: number; cancelled: number };
  };
  conversion_rates: { enquiry_to_quotation: number; quotation_to_booking: number; overall: number };
  lead_sources: (Stage & { source: string; conversion_rate: number })[];
  monthly_trend: (Stage & { month: string; month_name: string })[];
  direct_bookings: number;
}

export default function EnquiryFunnelPage() {
  const range = useRange({ from: yearRange(0).from, to: localToday() });
  const { from, to } = range.applied;

  const state = useReport<Funnel>(
    async () => (await reportsApi.enquiryFunnel({ from_date: from, to_date: to })).data,
    [from, to],
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={Filter}
          iconClass="text-indigo-600"
          title="Enquiry Funnel"
          subtitle="Of the enquiries received in the period: how many got a quotation and how many booked"
        >
          <HeaderTools>
            <RangeControls range={range} />
            {state.status !== 403 && (
              <ExportButtons
                build={() => state.data && buildExport(state.data, { from, to })}
                disabled={state.loading || !state.data}
              />
            )}
          </HeaderTools>
        </ReportHeader>

        <ReportBody state={state}>
          {(d) => <FunnelView d={d} />}
        </ReportBody>
      </div>
    </DashboardLayout>
  );
}

const STATUSES = [
  { key: "new", label: "New", cls: "bg-indigo-400" },
  { key: "qualified", label: "Qualified", cls: "bg-blue-400" },
  { key: "converted", label: "Quoted / booked", cls: "bg-emerald-400" },
  { key: "rejected", label: "Rejected", cls: "bg-red-400" },
] as const;

const sourceName = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** The explanation under the report, with the draft / cancelled counts for this period. */
const funnelNote = (f: Funnel["funnel"]) =>
  "Booked = the enquiry led to a confirmed, in-progress or completed event (directly or through its quotation)." +
  (f.events.draft > 0 ? ` ${f.events.draft} draft event${f.events.draft === 1 ? "" : "s"} not counted as booked.` : "") +
  (f.events.cancelled > 0 ? ` ${f.events.cancelled} cancelled event${f.events.cancelled === 1 ? "" : "s"} not counted.` : "") +
  " Events entered without an enquiry are shown as direct bookings, not in the funnel.";

/** Excel / Print: the funnel figures, enquiries by status, by lead source and per month. */
function buildExport(d: Funnel, period: { from: string; to: string }): ExportSpec {
  const f = d.funnel;
  const r = d.conversion_rates;
  const total = f.enquiries.total;
  const months = d.monthly_trend.some((m) => m.count > 0) ? d.monthly_trend : [];
  const share = (n: number) => (total ? Math.round((n / total) * 1000) / 10 : 0);

  return {
    title: "Enquiry Funnel",
    fileName: "enquiry-funnel",
    period,
    summary: [
      { label: "Enquiries", value: total, type: "number" },
      { label: "Got a quotation", value: f.quoted, type: "number" },
      { label: "Got a quotation (% of enquiries)", value: r.enquiry_to_quotation, type: "percent" },
      { label: "Booked", value: f.booked, type: "number" },
      { label: "Booked (% of enquiries)", value: r.overall, type: "percent" },
      { label: "Booked (% of quoted)", value: r.quotation_to_booking, type: "percent" },
      { label: "Direct bookings (without an enquiry)", value: d.direct_bookings, type: "number" },
    ],
    tables: [
      {
        title: "Enquiries by status",
        columns: [{ label: "Status" }, { label: "Enquiries", type: "number" }, { label: "Share", type: "percent" }],
        rows: total ? STATUSES.map((s) => [s.label, f.enquiries[s.key] ?? 0, share(f.enquiries[s.key] ?? 0)]) : [],
        totals: total ? ["Total", total, null] : undefined,
      },
      {
        title: "By lead source",
        columns: [
          { label: "Source" },
          { label: "Enquiries", type: "number" },
          { label: "Quoted", type: "number" },
          { label: "Booked", type: "number" },
          { label: "Booking rate", type: "percent" },
        ],
        rows: d.lead_sources.map((s) => [sourceName(s.source), s.count, s.quoted, s.booked, s.conversion_rate]),
      },
      {
        title: "Enquiries per month",
        columns: [{ label: "Month" }, { label: "Enquiries", type: "number" }, { label: "Booked", type: "number" }],
        rows: months.map((m) => [m.month_name, m.count, m.booked]),
        totals: months.length ? ["Total", months.reduce((t, m) => t + m.count, 0), months.reduce((t, m) => t + m.booked, 0)] : undefined,
      },
    ],
    notes: [funnelNote(f)],
  };
}

function FunnelView({ d }: { d: Funnel }) {
  const f = d.funnel;
  const r = d.conversion_rates;
  const total = f.enquiries.total;
  const steps = [
    { name: "Enquiries", value: total, share: 100, fill: "#6366f1" },
    { name: "Got a quotation", value: f.quoted, share: r.enquiry_to_quotation, fill: "#3b82f6" },
    { name: "Booked", value: f.booked, share: r.overall, fill: "#10b981" },
  ];
  const monthly = d.monthly_trend.map((m) => ({ month: m.month_name, enquiries: m.count, booked: m.booked }));

  return (
    <>
      <KpiGrid>
        <Kpi label="Enquiries" icon={Users} tone="indigo" value={total} sub="received in the period" />
        <Kpi label="Got a quotation" icon={FileText} tone="blue" value={f.quoted} sub={`${pct(r.enquiry_to_quotation)} of enquiries`} />
        <Kpi label="Booked" icon={CheckCircle} tone="emerald" value={f.booked} sub={`${pct(r.overall)} of enquiries · ${pct(r.quotation_to_booking)} of quoted`} />
        <Kpi label="Direct bookings" icon={Footprints} tone="stone" value={d.direct_bookings} sub="booked without an enquiry" />
      </KpiGrid>

      <Card title="Conversion funnel">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 py-2">
          {steps.map((step, i) => (
            <div key={step.name} className="flex flex-col sm:flex-row items-center gap-2 flex-1 min-w-0">
              <div className="w-full rounded-xl p-3 text-white text-center" style={{ backgroundColor: step.fill, opacity: 0.55 + 0.45 * (step.share / 100) }}>
                <div className="text-2xl font-bold tabular-nums">{step.value}</div>
                <div className="text-xs font-medium">{step.name}</div>
                {i > 0 && <div className="text-[11px] opacity-90">{pct(step.share)} of enquiries</div>}
              </div>
              {i < steps.length - 1 && (
                <>
                  <ArrowRight className="hidden sm:block w-4 h-4 text-stone-300 shrink-0" />
                  <ArrowDown className="sm:hidden w-4 h-4 text-stone-300 shrink-0" />
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Enquiries by status">
          {total === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center">No enquiries in this period</p>
          ) : (
            <ul className="space-y-2.5">
              {STATUSES.map((s) => {
                const count = f.enquiries[s.key] ?? 0;
                return (
                  <li key={s.key}>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-700">{s.label}</span>
                      <span className="font-semibold text-stone-800 tabular-nums">{count}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-1">
                      <div className={`h-full rounded-full ${s.cls}`} style={{ width: `${total ? (count / total) * 100 : 0}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="By lead source">
          {d.lead_sources.length === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center">No source data</p>
          ) : (
            <TableScroll minWidth={420}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className={`${th} text-left`}>Source</th>
                    <th className={`${th} text-right`}>Enquiries</th>
                    <th className={`${th} text-right`}>Quoted</th>
                    <th className={`${th} text-right`}>Booked</th>
                    <th className={`${th} text-right`}>Booking rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {d.lead_sources.map((s) => (
                    <tr key={s.source}>
                      <td className={`${td} text-stone-700 capitalize`}>{s.source.replace(/_/g, " ")}</td>
                      <td className={`${td} text-right`}>{s.count}</td>
                      <td className={`${td} text-right`}>{s.quoted}</td>
                      <td className={`${td} text-right text-emerald-700 font-medium`}>{s.booked}</td>
                      <td className={`${td} text-right text-stone-600`}>{pct(s.conversion_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>
      </div>

      {monthly.some((m) => m.enquiries > 0) && (
        <Card title="Enquiries per month" subtitle="By the month the enquiry came in">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={32} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="enquiries" name="Enquiries" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="booked" name="Booked" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Note>{funnelNote(f)}</Note>
    </>
  );
}
